import { Component, inject, OnInit } from "@angular/core";
import { OrderService } from "../services/order.service";
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { PaymentService } from "../services/payment.service";
import { PaymentMethod } from "../models/paymentmethod";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { TextService } from "../services/text.service";
import { ConfigService } from "../services/config.service";
import { TipObject } from "../models/config";
import { AsyncPipe, CommonModule, KeyValuePipe } from "@angular/common";
import { TtcurrencyPipe } from "../pipes/ttcurrency.pipe";
import { CartStore } from "../store/cart.store";
import {
  ButtonComponent as TtButtonComponent,
  OptionGroupComponent as TtOptionGroupComponent,
} from "@ticket-team-com/tt-component-library";

@Component({
  selector: "app-payment",
  templateUrl: "./payment.component.html",
  styleUrls: ["./payment.component.css"],
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    TtcurrencyPipe,
    CommonModule,
    KeyValuePipe,
    TtButtonComponent,
    TtOptionGroupComponent,
  ],
})
export class PaymentComponent implements OnInit {
  public form = new UntypedFormGroup({});
  public paymentMethods: PaymentMethod[] = [];
  public tip: TipObject = {
    enabled: false,
    articleId: "",
  };
  showOtherAmount = false;
  public orderService = inject(OrderService);
  private paymentService = inject(PaymentService);
  private router = inject(Router);
  private toastrService = inject(ToastrService);
  public textService = inject(TextService);
  private configService = inject(ConfigService);
  public cartStore = inject(CartStore);

  ngOnInit() {
    this.tip = this.configService.getConfig().tip;
    this.buildForm();
    this.setOrderedProducts();
  }

  setOrderedProducts() {
    if (this.cartStore.articles().length <= 0) {
      this.router.navigateByUrl("/");
    }
  }

  buildForm() {
    this.form.addControl("bank", new UntypedFormControl(""));
    // Register `method` synchronously so `formControlName` on the option group
    // binds at init; the options themselves arrive async below.
    this.form.addControl(
      "method",
      new UntypedFormControl("", [Validators.required]),
    );

    if (this.tip.enabled) {
      this.form.addControl("tip", new UntypedFormControl("0", []));
    }

    this.paymentService.getPaymentMethods().subscribe((response) => {
      this.paymentMethods = response;

      this.paymentMethods.sort((a) => {
        if (a.id.toUpperCase() === "ID" || a.id.toUpperCase() === "CR") {
          return -1;
        } else {
          return 1;
        }
      });
    });
  }

  confirmPayment() {
    this.orderService
      .createOrder("horeca@ticketteam.nl", "Horeca")
      .subscribe((response) => {
        if (!response.success) {
          this.toastrService.error(response.message);
          return;
        }
        this.orderService.setTransactionGuid(response.transaction_guid);
        this.orderService
          .createPayment(
            this.orderService.getTransactionGuid(),
            this.orderService.getPaymentMethod(),
          )
          .subscribe((response) => {
            window.location.href = response.url;
          });
      });
  }

  shouldEnableActionButtons() {
    return this.form.valid;
  }

  getCostForPaymentMethod(paymentMethod: PaymentMethod): number {
    return this.paymentService.getCosts(paymentMethod.id);
  }

  // Map the payment methods to squircle tiles for `tt-option-group`, keeping the
  // existing order (iDEAL / credit card first). The logo is shown as the tile
  // image and the surcharge as the `+price`; free methods simply show no price.
  getPaymentMethodOptions(): {
    label: string;
    value: string;
    price?: number;
    image?: string;
    imageAlt?: string;
  }[] {
    return this.paymentMethods.map((method) => ({
      label: method.name,
      value: method.id,
      price: this.getCostForPaymentMethod(method),
      image: method.image,
      imageAlt: method.name,
    }));
  }

  getPaymentMethod(paymentMethodId: string): PaymentMethod {
    for (const paymentMethod of this.paymentMethods) {
      if (paymentMethod.id === paymentMethodId) {
        return paymentMethod;
      }
    }
    return null;
  }

  onSelectPaymentMethod() {
    const newPaymentMethod = this.form.get("method").value;
    this.orderService.updatePaymentCosts(
      this.getPaymentMethod(newPaymentMethod),
      this.paymentService.getCosts(newPaymentMethod),
    );
    window.scrollTo(0, document.body.scrollHeight);
  }

  getExtraCostsifNotFree() {
    return this.orderService.getExtraCosts().filter((item) => {
      return item.price > 0;
    });
  }

  getGrandTotal(): number {
    return this.orderService.getGrandTotal();
  }

  getFeeAmounts(): number[] {
    return [
      0.05 * this.orderService.getProductTotal(),
      0.1 * this.orderService.getProductTotal(),
      0.2 * this.orderService.getProductTotal(),
    ];
  }

  setTip(value: number) {
    this.orderService.updateTip(value, this.tip.articleId);
  }

  onChangeTip() {
    let tip = this.form.get("tip").value;
    tip = +tip.replace(",", ".");
    if (tip < 0) {
      tip = 0;
    }
    this.orderService.updateTip(tip, this.tip.articleId);
  }

  decrement(id) {
    this.orderService.removeArticleByGeneratedId(id);
    this.setOrderedProducts();
  }

  increment(id) {
    this.orderService.addArticleByGeneratedId(id);
    this.setOrderedProducts();
  }

  selectPaymentMethod(id: string) {
    this.form.get("method").setValue(id);
    this.onSelectPaymentMethod();
  }
}
